// React
import { useState } from "react";

// MUI - Componentes
import { Box, Button, TextField } from "@mui/material";

// MUI - DatePicker
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Dayjs + locale español
import dayjs from "dayjs";
import "dayjs/locale/es";

// Formik + Yup
import { Formik } from "formik";
import * as Yup from "yup";

// Tus componentes propios
import Header from "../../components/Header";
import ModalCharge from "../../modal/modalCharge";
import ModalSucces from "../../modal/modalSucces";
import ModalError from "../../modal/modalError";
import ModalUniformes from "../../modal/uniformes/modalUniformes";
import { PostStUniformes } from "../../services/uniformes.services";

const RegistroStockUniforme = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationError, setRegistrationError] = useState(false);
  const [error, setError] = useState("");

  // Modal de uniformes
  const [openModalUniformes, setOpenModalUniformes] = useState(false);
  const [uniformeSeleccionado, setUniformeSeleccionado] = useState(null);

  const checkoutSchema = Yup.object().shape({
    id_uniforme: Yup.number().required("El uniforme es requerido"),
    fecha: Yup.string().required("La fecha de recepción es requerida"),
    cantidad: Yup.number()
      .min(1, "Mínimo 1")
      .max(500, "Máximo 500")
      .required("La cantidad es requerida"),
  });

  const handleSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      const dataToSend = {
        id_uniforme: values.id_uniforme,
        estado: "NUEVO",
        fecha: values.fecha,
        cantidad: values.cantidad,
      };

      const response = await PostStUniformes(dataToSend);

      setRegistrationSuccess(true);
      resetForm();
      setUniformeSeleccionado(null);
    } catch (error) {
      console.error(error);
      setRegistrationError(true);
      setError(error.message);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  return (
    <Box m="20px">
      <Header
        title="Registro de Stock"
        subtitle="Registrar entrada de uniformes al inventario"
      />

      <ModalCharge isLoading={isLoading} />
      <ModalSucces
        open={registrationSuccess}
        onClose={() => setRegistrationSuccess(false)}
      />
      <ModalError
        open={registrationError}
        onClose={() => setRegistrationError(false)}
        error={error}
      />

      <Formik
        initialValues={{
          id_uniforme: "",
          estado: "NUEVO",
          fecha: "",
          cantidad: "",
        }}
        validationSchema={checkoutSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <ModalUniformes
              open={openModalUniformes}
              onClose={() => setOpenModalUniformes(false)}
              onSelectUniforme={(uniforme) => {
                setUniformeSeleccionado(uniforme);
                setFieldValue("id_uniforme", uniforme.id);
                setOpenModalUniformes(false);
              }}
            />

            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              {/* INPUT UNIFORME */}
              <TextField
                variant="filled"
                label="Uniforme"
                name="id_uniforme"
                value={
                  uniformeSeleccionado
                    ? `${uniformeSeleccionado.descripcion} — Talla: ${uniformeSeleccionado.talla}`
                    : ""
                }
                onClick={() => setOpenModalUniformes(true)}
                onChange={() => {}}
                error={!!touched.id_uniforme && !!errors.id_uniforme}
                helperText={
                  touched.id_uniforme && errors.id_uniforme
                    ? errors.id_uniforme
                    : "Haz clic para buscar uniforme"
                }
                InputProps={{ readOnly: true }}
                sx={{
                  gridColumn: "span 1",
                  cursor: "pointer",
                  "& input": { cursor: "pointer" },
                }}
              />
              <TextField
                variant="filled"
                label="Cantidad"
                name="cantidad"
                type="number"
                value={values.cantidad}
                onChange={(e) =>
                  setFieldValue("cantidad", parseInt(e.target.value))
                }
                onBlur={handleBlur}
                error={!!touched.cantidad && !!errors.cantidad}
                helperText={
                  touched.cantidad && errors.cantidad
                    ? errors.cantidad
                    : "Cantidad de uniformes a ingresar"
                }
                inputProps={{ min: 1, max: 500 }}
                sx={{ gridColumn: "span 1" }}
              />

              {/* ESTADO (solo lectura, siempre NUEVO) */}
              <TextField
                variant="filled"
                label="Estado"
                name="estado"
                value="NUEVO"
                InputProps={{ readOnly: true }}
                sx={{
                  gridColumn: "span 1",
                  "& input": {
                    color: "rgb(206, 220, 0)",
                    fontWeight: "bold",
                  },
                }}
              />

              {/* FECHA RECEPCIÓN - DatePicker en español */}
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="es"
              >
                <DatePicker
                  label="Fecha de ingreso a Stock"
                  value={values.fecha ? dayjs(values.fecha) : null}
                  onChange={(newValue) => {
                    setFieldValue(
                      "fecha",
                      newValue ? newValue.format("YYYY-MM-DD HH:mm:ss") : "",
                    );
                  }}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      error: !!touched.fecha && !!errors.fecha,
                      helperText:
                        touched.fecha && errors.fecha
                          ? errors.fecha
                          : "Fecha en que se recibe el uniforme",
                      onBlur: handleBlur,
                      name: "fecha",
                    },
                  }}
                  sx={{ gridColumn: "span 1" }}
                />
              </LocalizationProvider>
            </Box>

            {/* BOTÓN */}
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                sx={{
                  backgroundColor: "rgb(206, 220, 0)",
                  border: "none",
                  color: "black",
                  height: "7vh",
                  width: "10vw",
                  fontSize: "18px",
                  fontWeight: "bold",
                  borderRadius: "20px",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#bac609" },
                }}
                variant="contained"
              >
                Registrar Stock
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default RegistroStockUniforme;
